/**
 * Chinese (Simplified) display text for the Parent Portal pathway areas:
 * Pathway Navigator, My Study Plan (senior pathway), HSC planner, Explore and
 * the Creative Code Challenge pages.
 *
 * Key = the exact English source string in code. Course, teacher and backend
 * content (course titles, pathway graph text, served consent and rubric
 * wording) is never translated here.
 */
export const ZH_PATHWAY: Readonly<Record<string, string>> = {
  'Version {version}': '版本 {version}',
  'Draft document — not legally approved': '草稿文件——尚未经法律审核通过',
  'This wording has not been reviewed by Australian legal or privacy counsel. It is in place so the consent process can be tested, and it grants nothing. Your choices below are recorded exactly as you make them.':
    '这份文字尚未经澳大利亚法律或隐私顾问审核。它仅用于测试同意流程，不授予任何权利。你在下方所做的选择会如实记录。',
  'You are under 18, so a parent or guardian must know about this. They do not need their own account.':
    '你未满 18 岁，所以需要让家长或监护人知道这件事。他们不需要自己的账号。',
  '{name} hasn’t started building yet.': '{name} 还没有开始创作。',
  'Nobody has started building yet.': '还没有人开始创作。',
  'Open the challenge →': '打开挑战赛 →',
  'Your browser cannot play this video.': '你的浏览器无法播放这个视频。',
  'Open it directly': '直接打开',
  'Start here': '从这里开始',
  'How the Creative Code Challenge works': 'Creative Code 挑战赛如何进行',
  'A short walkthrough of what your child builds, where they build it, and what gets sent in. Most families are doing this for the first time.':
    '简短介绍孩子要做什么、在哪里做，以及最终提交什么。大多数家庭都是第一次参加。',
  Signed: '已签署',
  'signed {date}': '{date} 签署',
  'valid until {date}': '有效期至 {date}',
  'until {date}': '至 {date}',
  none: '无',
  'Update these choices': '更新这些选择',
  'Signing again replaces all of the choices above with whatever you submit. The form opens with what is recorded here.':
    '再次签署会用你提交的内容替换上面的所有选择。表单打开时会带出这里已记录的内容。',
  'Loading challenge': '正在加载挑战赛',
  'Checking the current edition and registration window…': '正在查看当前赛季和报名时间…',
  'Could not load this challenge': '无法加载这个挑战赛',
  'We couldn’t check the edition or its registration window. Entry actions will stay hidden until this check succeeds.':
    '我们无法确认赛季及其报名时间。在检查成功之前，报名操作会保持隐藏。',
  'Challenge unavailable': '挑战赛暂不可用',
  'There is no challenge edition at this address.': '这个地址下没有挑战赛赛季。',
  'Back to the Portal': '返回家长中心',
  'Creative Code Challenge · Junior': 'Creative Code 挑战赛 · 少年组',
  'An online creative coding competition for ages 8–14. Your child turns an original idea into a playable browser project, explains how they made it, and is judged fairly against a published 100-point guide.':
    '面向 8–14 岁孩子的线上创意编程比赛。孩子把原创想法做成可在浏览器中玩的作品，讲解自己是怎么做的，并按公开的 100 分评分标准公平评审。',
  'Join from home': '在家参加',
  '1 project': '1 个作品',
  'Game or web': '游戏或网页',
  '60–90 sec': '60–90 秒',
  'Project pitch': '作品讲解',
  '100 points': '100 分',
  'Published rubric': '公开评分标准',
  'Where the competition is now': '比赛目前进行到哪里',
  'The competition is available, but we could not check every family entry.':
    '比赛可以参加，但我们无法查看每一份家庭报名。',
  'Checking your family’s current entries…': '正在查看你家当前的报名…',
  'Submit {from} — {to}': '提交时间 {from} — {to}',
  'Results {date}': '公布结果 {date}',
  'Registration is not open at the moment, so no new entries can be started or paid for.':
    '目前未开放报名，因此无法开始或支付新的报名。',
  'Competition journey': '比赛流程',
  'The whole journey': '完整流程',
  'What happens, and when': '做什么，什么时候做',
  'You do not need to understand the whole platform today. Follow these four stages; this page will keep showing where the competition has reached.':
    '你今天不需要弄懂整个平台。跟着这四个阶段走就行；本页会一直显示比赛进行到哪一步。',
  'Competition timeline': '比赛时间线',
  Now: '当前',
  'Loading the competition timeline…': '正在加载比赛时间线…',
  'What children practise': '孩子会练习什么',
  'Turn an idea into something real': '把想法变成真实作品',
  'Your child makes a project another person can open, try and understand.':
    '孩子做出的作品，别人可以打开、试玩并看懂。',
  'Explain their own decisions': '讲清自己的决定',
  'The pitch is about clear thinking and ownership — never accent or fancy vocabulary.':
    '讲解看重的是思路清晰和作品确实出自自己——不看口音，也不看词汇是否华丽。',
  'Learn from a clear rubric': '从清晰的评分标准中学习',
  'Families can see the same marking guide the judges use before the child submits.':
    '孩子提交之前，家庭就能看到评委使用的同一份评分标准。',
  'Your family’s entries': '你家的报名',
  'Each child has their own entry, project and submission. Choose the action beside their name — nothing is shared between siblings.':
    '每个孩子都有自己的报名、作品和提交。选择孩子姓名旁边的操作即可——兄弟姐妹之间不会共享任何内容。',
  'Loading your children…': '正在加载你的孩子…',
  'We could not load your children.': '我们无法加载你的孩子信息。',
  'No children on your family profile yet.': '你的家庭资料中还没有孩子。',
  'Add one first': '先添加一个孩子',
  'Could not check this child’s entry': '无法查看这个孩子的报名情况',
  'Open and retry': '打开并重试',
  Entered: '已报名',
  'Open challenge page': '打开挑战赛页面',
  'Started — not paid yet': '已开始——尚未付款',
  'Finish registering': '完成报名',
  'Not entered': '未报名',
  'Register this child': '为这个孩子报名',
  'Retry entry check': '重新检查报名',
  'Could not open {name}’s challenge page. Try again.': '无法打开 {name} 的挑战赛页面，请重试。',
  'Open my challenge page →': '打开我的挑战赛页面 →',
  'Open {name}’s challenge page →': '打开 {name} 的挑战赛页面 →',
  'Registration closed': '报名已截止',
  'How to enter and build': '如何报名和创作',
  'What happens next': '接下来会发生什么',
  'What entering involves': '报名需要做什么',
  'Most families are doing this for the first time. Here is the whole thing, start to finish.':
    '大多数家庭都是第一次参加。下面是从头到尾的完整流程。',
  'What your child builds': '孩子要做什么作品',
  'One project, either kind. Both run in a browser.':
    '只做一个作品，两种类型任选。两种都在浏览器中运行。',
  'What gets submitted': '要提交什么',
  'Where your child submits': '孩子在哪里提交',
  'Your child submits from': '孩子从',
  'their own account': '他们自己的账号',
  ', not from yours — the entry has to be theirs. They sign in to Learn and open':
    '提交，而不是从你的账号提交——参赛作品必须属于孩子本人。他们登录 Learn 后打开',
  Use: '点击上方的',
  'Open [child’s name]’s challenge page': '打开 [孩子姓名] 的挑战赛页面',
  'above. Airbotix signs that child in and opens': '。Airbotix 会让该孩子登录，并打开',
  'in a new tab, while this parent page stays open. To continue on a different device, use':
    '，在新标签页中打开，本家长页面保持不变。如需换设备继续，请使用',
  'Sign in with QR': '扫码登录',
  'beside the child’s entry.': '，它位于孩子的报名旁边。',
  'Judging and public showcase': '评审与公开展示',
  'How it is judged': '如何评审',
  'Every entry is scored out of {total} by each judge independently. This is the whole breakdown — nothing is scored that is not on this list.':
    '每份作品满分 {total} 分，由每位评委独立打分。这就是完整的评分明细——不在这份清单上的内容都不会计分。',
  '{points} marks': '{points} 分',
  'We could not load the marking guide just now. The criteria have not changed.':
    '暂时无法加载评分标准，评分标准没有变化。',
  'The public Creator Showcase': '公开的创作者展示',
  'Approved entries appear in a public Showcase, and the public vote there decides the Junior People’s Choice award only — it does not change the judges’ scores.':
    '通过审核的作品会出现在公开展示中，那里的公众投票只决定少年组人气奖——不会改变评委的评分。',
  'What appears is only what you permitted in the media release. If you granted nothing, your child’s work is judged exactly the same and simply is not shown publicly.':
    '展示的内容仅限于你在媒体授权中允许的部分。如果你没有授权任何内容，孩子的作品仍会得到完全相同的评审，只是不会公开展示。',
  'Open the public Showcase →': '打开公开展示 →',
  'This competition is being prepared': '比赛正在筹备中',
  'You can learn how it works now. Registration will appear here when it opens.':
    '你现在就可以了解比赛如何进行。报名开放后会在这里出现。',
  'Your family is in': '你家已报名',
  'Start building — there is no need to wait': '马上开始创作——不用等',
  'Your child can begin their project now and keep improving it before submissions open.':
    '孩子现在就可以开始做作品，并在开放提交之前不断改进。',
  'Registration open': '报名开放中',
  'Choose a child to enter': '选择要报名的孩子',
  'Registration, consent and payment all happen from the child row below.':
    '报名、同意和付款都在下方孩子所在的那一行进行。',
  'Submissions open': '开放提交',
  'It is time to finish and submit': '该完成并提交了',
  'Your child submits from their own account before the closing date shown below.':
    '孩子需在下方显示的截止日期前，从自己的账号提交。',
  'Review and judging': '审核与评审',
  'Entries are with the review team and judges': '作品正由审核团队和评委处理',
  'Approved entries can appear in the Creator Showcase according to each family’s media choices.':
    '通过审核的作品可按各家庭的媒体选择出现在创作者展示中。',
  'Results ready': '结果已出',
  'Your child’s private feedback is ready': '孩子的专属反馈已准备好',
  'Open their entry to see the result and judge feedback available to your family.':
    '打开他们的报名，查看结果和提供给你家的评委反馈。',
  'Results published': '结果已公布',
  'The challenge is complete': '挑战赛已结束',
  'Results and approved Creator Showcase entries are now available.':
    '结果和通过审核的创作者展示作品现已可以查看。',
  'Register and get ready': '报名并做好准备',
  'Open now': '现已开放',
  'Choose a child, record both consent decisions and complete the entry.':
    '选择孩子，记录两项同意决定，并完成报名。',
  'Build and submit': '创作并提交',
  'Build one browser project, record the pitch and submit from the child’s account.':
    '做一个浏览器作品，录制讲解视频，并从孩子的账号提交。',
  'Review, showcase and judging': '审核、展示与评审',
  'After {date}': '{date} 之后',
  'Entries are safety-reviewed, approved work can enter the Showcase, and judges use the published rubric.':
    '作品会先经过安全审核，通过的作品可进入展示，评委按公开的评分标准评审。',
  'Results and private feedback': '结果与专属反馈',
  'Your family can see your child’s feedback without seeing another child’s private marks.':
    '你家可以看到孩子自己的反馈，但看不到其他孩子的评分。',
  'A Game Project': '游戏作品',
  'A game your child designs and builds themselves — something another child can pick up and play.':
    '孩子自己设计并制作的游戏——别的孩子拿起来就能玩。',
  'An Interactive Web Project': '互动网页作品',
  'Something that responds to whoever is using it — a tool, a toy, a story, a simulation. Your child builds this in the Website Studio, inside Creative Code Studio.':
    '能对使用者做出回应的作品——工具、玩具、故事或模拟都可以。孩子在 Creative Code Studio 的 Website Studio 中制作。',
  'The playable project': '可玩的作品',
  'The game or interactive web project itself, running in a browser.':
    '游戏或互动网页作品本身，在浏览器中运行。',
  'A 60–90 second pitch video': '60–90 秒的讲解视频',
  'Your child explains their project in English, in their own words. You may help with recording and uploading — not with what they say.':
    '孩子用英语、用自己的话讲解作品。你可以帮忙录制和上传——但不能代替孩子说内容。',
  'Evidence of one change they made': '一次修改的证据',
  'One thing they tried, tested and changed. This shows the thinking, not just the finished screen.':
    '他们尝试、测试并修改过的一件事。它展示的是思考过程，而不只是最终画面。',
  'A creator card': '创作者卡片',
  'The short profile shown beside the work.': '显示在作品旁边的简短介绍。',
  'Your media choices': '你的媒体选择',
  'Already recorded when you signed the media release. You can change them while registration is open.':
    '在你签署媒体授权时已经记录。报名开放期间你可以修改。',
  'Your child can start building today': '孩子今天就可以开始创作',
  'Use “Open [child]’s challenge page” beside their name. Airbotix creates the child session and opens their challenge page in a new tab while this parent page stays open. They choose Game or Interactive Web Project and start immediately; only sending the finished entry waits for the submission window.':
    '使用孩子姓名旁边的“打开 [孩子] 的挑战赛页面”。Airbotix 会创建孩子的登录会话，并在新标签页打开他们的挑战赛页面，本家长页面保持不变。孩子选择游戏或互动网页作品后即可马上开始；只有提交完成的作品要等到提交时间开放。',
  'Have a look at Creative Code Studio yourself': '自己去看看 Creative Code Studio',
  'Use the Starter Lab session that came with your entry': '使用报名附赠的 Starter Lab 课时',
  'Your entry includes one Creative Code Studio Starter Lab session and 500 Stars. The Stars are a limited in-app balance, not money, and they are already in your family wallet.':
    '你的报名包含一节 Creative Code Studio Starter Lab 课时和 500 星星。星星是应用内的有限余额，不是钱，并且已经存入你的家庭钱包。',
  'Practise the pitch early, not the night before': '提前练习讲解，不要等到前一晚',
  'The 60–90 second explanation is scored on how clearly your child explains their own work — never on accent, vocabulary, or the language spoken at home.':
    '这段 60–90 秒的讲解，评分看的是孩子讲清自己作品的程度——不看口音、词汇，也不看家里说什么语言。',
  'Submit inside the window': '在提交时间内提交',
  'Your child submits from their own account. Entries cannot be submitted after the window closes, so do not leave it to the final evening.':
    '孩子从自己的账号提交。提交时间结束后无法再提交，所以不要拖到最后一晚。',
  'Every entry is scored against the same 100-point rubric, by each judge independently.':
    '每份作品都按同一份 100 分的评分标准，由每位评委独立打分。',
  'English is scored only on how clearly your child explains their work — never on accent, vocabulary, or the language spoken at home.':
    '英语只看孩子讲清作品的程度——不看口音、词汇，也不看家里说什么语言。',
  'An entry that shows neither your child’s face nor their voice is judged in exactly the same way as one that shows both.':
    '既不出现孩子的脸、也不出现孩子声音的作品，与两者都出现的作品，评审方式完全相同。',
  'Choose a child above to see their report.': '请先在上方选择一个孩子，查看他们的报告。',
  'Loading this report…': '正在加载这份报告…',
  'Your child’s private report': '孩子的专属报告',
  'Private to your family. Nobody else sees this page.': '仅你的家庭可见，其他人看不到这个页面。',
  'Could not load your children': '无法加载你的孩子信息',
  'The report picker will stay hidden until this check succeeds.':
    '在检查成功之前，报告选择器会保持隐藏。',
  'No children yet': '还没有孩子',
  'No children are on your family profile.': '你的家庭资料中没有孩子。',
  'Whose report?': '查看谁的报告？',
  'Not published yet': '尚未公布',
  'Results are expected on {date}.': '预计在 {date} 公布结果。',
  'Back to the Portal →': '返回家长中心 →',
  'Could not load this report': '无法加载这份报告',
  'This is a failed request, not a missing report — please try again.':
    '这是请求失败，并不是没有报告——请重试。',
  'No report for this child': '这个孩子没有报告',
  'We could not load this report. Please try again.': '我们无法加载这份报告，请重试。',
  'The results for this challenge have not been published yet, so there is no report to show yet. It becomes available once judging closes and the results are finalised.':
    '这场挑战赛的结果尚未公布，所以暂时没有可显示的报告。评审结束、结果最终确定后就可以查看。',
  'There is no judges’ report for this child in this challenge. Please contact Airbotix if you expected one.':
    '这场挑战赛中没有这个孩子的评委报告。如果你本来应该有，请联系 Airbotix。',
  'We could not open a report for this child in this challenge. Check the link you followed, or choose a different child.':
    '我们无法打开这个孩子在这场挑战赛中的报告。请检查你点击的链接，或选择其他孩子。',
  Game: '游戏',
  'Interactive web project': '互动网页作品',
  'Results finalised {date}.': '结果已于 {date} 最终确定。',
  'The one change they made': '他们做的那一次修改',
  'What this is': '这份报告是什么',
  'Marks not shown': '分数暂不显示',
  'These marks were given under version {scored} of the judging rubric, and the rubric published now is version {current}. Airbotix will not print a mark against a maximum your child was not scored on, so the marks are held back here rather than shown against the wrong scale.':
    '这些分数是按评分标准第 {scored} 版给出的，而目前公布的评分标准是第 {current} 版。Airbotix 不会把分数印在孩子并未据以评分的满分上，所以这里暂不显示分数，以免对应错误的评分尺度。',
  'These marks were given under a version of the judging rubric that was not recorded, and the rubric published now is version {current}. Airbotix will not print a mark against a maximum your child was not scored on, so the marks are held back here rather than shown against the wrong scale.':
    '这些分数所依据的评分标准版本没有记录，而目前公布的评分标准是第 {current} 版。Airbotix 不会把分数印在孩子并未据以评分的满分上，所以这里暂不显示分数，以免对应错误的评分尺度。',
  'Nothing about the judging has changed and nothing has been lost — this is a display rule, not a result. Please contact Airbotix and we will send this report against the rubric it was actually marked on.':
    '评审本身没有任何变化，也没有任何内容丢失——这只是显示规则，不是结果。请联系 Airbotix，我们会按实际评分所用的评分标准把这份报告发给你。',
  'What this entry showed': '这份作品展示了什么',
  'One part for each of the six things the judges marked, banded against that part’s own maximum.':
    '评委评分的六个方面各占一部分，按该部分自己的满分划分等级。',
  'Judges’ average for this part: {mark}': '这一部分的评委平均分：{mark}',
  'The four bands': '四个等级',
  'The judges’ marks together': '评委评分汇总',
  '1 judge’s marks stand behind this report.': '这份报告基于 1 位评委的评分。',
  '{count} independent judges’ marks stand behind this report.':
    '这份报告基于 {count} 位独立评委的评分。',
  'Average total: {mark}.': '平均总分：{mark}。',
  'Fewer than {min} judges’ marks stand behind this report. Airbotix normally looks for at least {min} independent opinions, so please read this as a lighter picture than usual.':
    '这份报告所依据的评委评分少于 {min} 位。Airbotix 通常希望至少有 {min} 位独立评委的意见，所以请把它看作比平时更简略的参考。',
  'What was marked': '评分项目',
  'Judges’ average': '评委平均分',
  'What each judge wrote': '每位评委的评语',
  'Judges read entries independently. They are shown here as “Judge 1”, “Judge 2” and so on — Airbotix does not publish who judged an entry.':
    '评委各自独立阅读作品。这里显示为“评委 1”“评委 2”等——Airbotix 不公布是谁评审了作品。',
  'Total {mark}': '总分 {mark}',
  'This judge recorded marks without a written comment.': '这位评委只记录了分数，没有写评语。',
  'Ideas for what’s next': '接下来的建议',
  'Suggestions that follow from the two parts of this entry with the most room to grow. They are ideas, not requirements.':
    '这些建议来自这份作品中最有提升空间的两个方面。它们只是想法，不是要求。',
  'For ages {min}–{max}.': '适合 {min}–{max} 岁。',
  'See this course →': '查看这门课程 →',
  'Your details': '你的信息',
  'Parent/Guardian Details': '家长/监护人信息',
  'You manage your own account, so you sign this form for yourself.':
    '你管理自己的账号，所以这份表格由你本人签署。',
  'Full name': '全名',
  'Signing as': '签署身份',
  'Relationship to student': '与学生的关系',
  'Select…': '请选择…',
  Email: '邮箱',
  'Signature / electronic confirmation': '签名 / 电子确认',
  Date: '日期',
  'Recorded by Airbotix when your signature is received. You do not type this.':
    '收到你的签名时由 Airbotix 记录，无需你自己填写。',
  'Add your full name': '请填写你的全名',
  'Keep this to 120 characters or fewer': '请控制在 120 个字符以内',
  'Choose your relationship to the student': '请选择你与学生的关系',
  'Add your email': '请填写你的邮箱',
  'Enter an email we can reach you at': '请输入我们可以联系到你的邮箱',
  'Keep this to 254 characters or fewer': '请控制在 254 个字符以内',
  'Draw or type your signature': '请手写或输入你的签名',
  'Type your signature': '输入你的签名',
  'Draw my signature instead': '改为手写签名',
  'Sign here with your finger, mouse or trackpad': '请用手指、鼠标或触控板在这里签名',
  'Signature captured': '已获取签名',
  'Clear signature': '清除签名',
  'Type my signature instead': '改为输入签名',
  'Form unavailable': '表单暂不可用',
  'This form cannot be shown right now, so nothing can be accepted. Reload the page — if it keeps happening, contact Airbotix rather than accepting anything.':
    '这份表格暂时无法显示，所以无法接受任何条款。请刷新页面——如果问题持续出现，请联系 Airbotix，不要接受任何内容。',
  'This form cannot be shown right now, so nothing can be signed. Reload the page — if it keeps happening, contact Airbotix rather than signing anything.':
    '这份表格暂时无法显示，所以无法签署任何内容。请刷新页面——如果问题持续出现，请联系 Airbotix，不要签署任何内容。',
  'Tick the declaration to accept these terms': '请勾选声明以接受这些条款',
  'Tick the box to say a parent or guardian knows you are entering':
    '请勾选此框，表示家长或监护人知道你要参赛',
  'Recording your acceptance…': '正在记录你的接受…',
  'Accept the competition terms →': '接受比赛条款 →',
  'This challenge is not taking entries at the moment, so nothing can be accepted right now.':
    '这个挑战赛目前不接受报名，所以现在无法接受任何条款。',
  'These are the choices currently on record. Signing again replaces all six of them with whatever is set below, so leave the ones you are happy with as they are.':
    '这些是目前已记录的选择。再次签署会用下方设置的内容替换全部六项，所以你满意的选项请保持不变。',
  'Your choices — each one is recorded separately': '你的选择——每一项都单独记录',
  'Add the name to publish with your child’s work': '请填写与孩子作品一起公布的名字',
  'Keep this to 60 characters or fewer': '请控制在 60 个字符以内',
  'Permission ends automatically on': '授权将于',
  ', 12 months from today, when this consent expires. You can end it sooner at any time by emailing us — you do not have to wait for that date.':
    '自动结束，即从今天起 12 个月后，也就是本同意书到期时。你随时可以通过给我们发邮件提前终止——不必等到那一天。',
  'Recording your choices…': '正在记录你的选择…',
  'Sign the media release →': '签署媒体授权 →',
  'This challenge is not taking entries at the moment, so nothing can be signed right now.':
    '这个挑战赛目前不接受报名，所以现在无法签署任何内容。',
  'Signing records your choices as made — including any you decline. You will sign the competition terms as a separate step.':
    '签署会如实记录你的选择——包括你拒绝的项目。比赛条款将在单独的一步中签署。',
  'Both consent forms need a parent or guardian signature before the entry fee can be paid. The outstanding form is open below.':
    '在支付报名费之前，两份同意书都需要家长或监护人签名。尚未签署的表格已在下方打开。',
  'This form was updated while it was open. Reload the page and read the current version before signing — nothing was recorded.':
    '这份表格在你打开期间已更新。请刷新页面，阅读当前版本后再签署——没有记录任何内容。',
  'This challenge is not taking entries at the moment, so nothing can be signed or paid right now. Check the challenge page for the next registration window.':
    '这个挑战赛目前不接受报名，所以现在无法签署或付款。请查看挑战赛页面了解下一个报名时间。',
  'This child is already entered in this challenge.': '这个孩子已经报名了这场挑战赛。',
  'Finish setting up your family profile before registering a child.':
    '请先完成家庭资料设置，再为孩子报名。',
  'We could not find this challenge. Check the link you followed.':
    '我们找不到这个挑战赛。请检查你点击的链接。',
  'Only a parent or guardian can register a child for this challenge.':
    '只有家长或监护人才能为孩子报名这场挑战赛。',
  'Tick the box to say a parent or guardian knows you are entering. Nothing was recorded.':
    '请勾选此框，表示家长或监护人知道你要参赛。没有记录任何内容。',
  'A photo or video of your face needs written consent from a parent or guardian. Nothing was recorded.':
    '公开你脸部的照片或视频，需要家长或监护人的书面同意。没有记录任何内容。',
  'You are under 15, so a parent or guardian must agree before we publish anything. Only staff and judges can see your work. Nothing was recorded.':
    '你未满 15 岁，所以在我们公开任何内容之前，必须经过家长或监护人同意。只有工作人员和评委能看到你的作品。没有记录任何内容。',
  'Before you pay, confirm that you are allowed to use this payment card. Press Pay again to do it.':
    '付款之前，请确认你被允许使用这张支付卡。再次点击“付款”即可确认。',
  'ages {range}': '{range} 岁',
  'About {range}': '约 {range}',
  'Read guide': '阅读指南',
  '{title} guide cover': '{title} 指南封面',
  'Family guide': '家庭指南',
  "Loading today's picks": '正在加载今日推荐',
  'New picks daily': '每日更新',
  "Today's picks are refreshing.": '今日推荐正在更新。',
  'Browse guides': '浏览指南',
  'Stage {number}': '阶段 {number}',
  'Story Foundations': '故事基础',
  'Understand meaning, connect events and express a complete idea.':
    '理解含义、串联事件，并表达一个完整的想法。',
  'Ages 5–6': '5–6 岁',
  'Ages 7–8': '7–8 岁',
  'Ages 8–10': '8–10 岁',
  'Usually 8+': '通常 8 岁以上',
  'Ages 10–12': '10–12 岁',
  'Ages 12–14': '12–14 岁',
  'Ages 14–16': '14–16 岁',
  'Ages 16–18': '16–18 岁',
  'Listen, sequence, predict and retell.': '倾听、排序、预测并复述。',
  'Explain cause, clues and character choices.': '解释原因、线索和角色的选择。',
  'Independent Creator': '独立创作者',
  'Plan for an audience, build a first version and improve it.':
    '为观众做规划，做出第一个版本并不断改进。',
  'Design and test a complete story world.': '设计并测试一个完整的故事世界。',
  'Build, debug and pitch a first AI-assisted project.': '制作、调试并讲解第一个 AI 辅助作品。',
  'User & Product Builder': '用户与产品创造者',
  'Use real feedback to design and redesign a complete product.':
    '利用真实反馈设计并重新设计一个完整的产品。',
  'Build frontend products for a specific user.': '为特定用户制作前端产品。',
  'Use design thinking to build full-stack inventions.': '运用设计思维制作全栈发明。',
  'Launch & Leadership': '发布与领导力',
  'Ship reliably, validate value and lead the next decision.':
    '稳定发布、验证价值，并带领团队做下一个决定。',
  'Use Git, teamwork and testing to launch reliably.': '用 Git、团队协作和测试稳定地发布作品。',
  'Lead a responsible pilot and evidence-based pitch.': '带领一次负责任的试点，并做有依据的路演。',
  '4 complete term courses': '4 门完整学期课程',
  'Airbotix learning pathway': 'Airbotix 学习路径',
  'One pathway from first stories to real AI products.':
    '一条从第一个故事到真实 AI 产品的学习路径。',
  'Eight Levels give your family a long-term plan through reading, creative building, product thinking and responsible leadership—not a collection of disconnected classes.':
    '八个等级为你的家庭提供一份长期规划，涵盖阅读、创意制作、产品思维和负责任的领导力——而不是一堆互不相关的课程。',
  "Explore your child's pathway": '了解孩子的学习路径',
  'Eight learning levels': '八个学习等级',
  'Explore Level {level}, {identity}': '了解第 {level} 级：{identity}',
  'Every term leaves evidence parents can understand.': '每个学期都会留下家长看得懂的成果证据。',
  'Meaning · Make · Improve · Own — plus a final Challenge and Showcase. Age guides the first conversation; readiness and work evidence decide the Level.':
    '理解 · 创作 · 改进 · 拥有——外加最后的挑战和展示。年龄只是第一次沟通的参考；准备程度和作品证据才决定等级。',
  'Holiday classes stay optional': '假期班保持自愿参加',
  'View more games': '查看更多游戏',
  'Related courses': '相关课程',
  'Self-paced courses for kids': '孩子自学课程',
  'View self-paced courses': '查看自学课程',
  'View tutoring': '查看私教辅导',
  'Family guides': '家庭指南',
  'View more guides': '查看更多指南',
  Challenges: '挑战赛',
  'Exam prep': '备考',
  'View more exam prep': '查看更多备考内容',
  'Learn independently': '自主学习',
  'View tutor': '查看导师',
  'View exam prep': '查看备考内容',
  'Open challenge': '打开挑战赛',
  'Student work': '学生作品',
  'Play game': '玩游戏',
  '{title}, Airbotix tutor': '{title}，Airbotix 导师',
  '{title} preview': '{title} 预览',
  'Related to: {work}': '相关作品：{work}',
  '{title} carousel controls': '{title} 轮播控制',
  'Show previous {title}': '显示上一组{title}',
  'Show more {title}': '显示更多{title}',
  'Loading {title} preview': '正在加载{title}预览',
  '{title} previews': '{title}预览',
  'Open a finished project and try it. Where a course is linked, you can see how your child could learn to make their own. Some examples are made by the Airbotix team.':
    '打开一个已完成的作品并试玩。如果关联了课程，你可以看到孩子如何学会做出自己的作品。部分示例由 Airbotix 团队制作。',
  'Finding courses linked to the projects above…': '正在查找与上方作品关联的课程…',
  'Related courses could not be loaded. Browse all courses or try again later.':
    '无法加载相关课程。请浏览全部课程，或稍后再试。',
  'These courses are linked to the projects above. Check the ages, learning outcomes and available class times.':
    '这些课程与上方的作品相关联。请查看适合的年龄、学习成果和可选的上课时间。',
  'No related courses match the current filters. Browse all courses to see other options.':
    '没有符合当前筛选条件的相关课程。请浏览全部课程查看其他选择。',
  'Online, anytime. Your child learns independently, at their own pace, with step-by-step lessons. No live class or teacher-led session.':
    '线上学习，随时可学。孩子按自己的节奏独立学习，跟着分步骤的课节进行。没有直播课，也没有老师带课。',
  'One-time course unlock per learner. AI messages use extra Stars.':
    '每位学习者一次性解锁课程。AI 对话会额外消耗星星。',
  'Learning and family marketplace': '学习与家庭市集',
  'Class format': '上课形式',
  'Learn with a teacher at a local venue. Choose a city to see published class times and planned courses.':
    '在本地场地与老师一起学习。选择城市，查看已发布的上课时间和计划中的课程。',
  'Join a teacher live from home at a scheduled time. These are separate from self-paced courses.':
    '在约定的时间在家与老师在线上直播互动。这与自学课程是分开的。',
  'Planned courses have no confirmed date or venue yet. Other course formats are listed on the full courses page.':
    '计划中的课程还没有确定日期和场地。其他形式的课程列在完整课程页面。',
  'A live online course appears here only when an online class is published.':
    '只有发布了线上班级，这里才会出现线上直播课程。',
  'Some class information could not load.': '部分班级信息无法加载。',
  'Class details are unavailable. Please retry above before choosing a class.':
    '班级详情暂不可用。选择班级之前，请先在上方重试。',
  'No related live online classes match these projects right now. Browse self-paced courses below to learn anytime.':
    '目前没有与这些作品相关的线上直播班。可以浏览下方的自学课程，随时开始学习。',
  'No related class times or planned courses match these projects in {city} yet. Browse all courses for more options.':
    '{city} 暂时没有与这些作品相关的上课时间或计划中的课程。请浏览全部课程查看更多选择。',
  'No related class times or planned courses match these projects for these cities yet. Browse all courses for more options.':
    '这些城市暂时没有与这些作品相关的上课时间或计划中的课程。请浏览全部课程查看更多选择。',
  'HSC subject planner': 'HSC 选科规划',
  'Plan senior subjects and study pathways': '规划高中科目与学习路径',
  'Build a website': '制作一个网站',
  'Manage your child’s entry.': '管理孩子的报名。',
  'Direct payment': '直接付款',
  'Price unavailable': '价格暂不可用',
  'Choose course': '选择课程',
  'Choose child': '选择孩子',
  'Family setup needed': '需要先设置家庭',
  'Create your family before saving an HSC plan': '请先创建家庭，再保存 HSC 计划',
  'Set up my family →': '设置我的家庭 →',
  'HSC family planner': 'HSC 家庭规划',
  'Keep the facts together. Plan the next assessment.': '把事实记录在一起，规划下一次考核。',
  'Save school assessment details for one child at a time. Running results are school progress only—not a predicted HSC mark, Band or ATAR.':
    '一次为一个孩子保存学校考核信息。累计成绩只代表学校内的进度——不是预测的 HSC 分数、Band 或 ATAR。',
  'Loading the HSC course list…': '正在加载 HSC 课程列表…',
  'We could not load the HSC course list, so subjects cannot be added right now.':
    '我们无法加载 HSC 课程列表，所以暂时无法添加科目。',
  'No published courses': '没有已发布的课程',
  'No HSC courses are available yet. Existing plans remain visible, but subjects cannot be added.':
    '目前还没有可用的 HSC 课程。已有的计划仍可查看，但无法添加科目。',
  'We could not load your children. Plan actions will stay hidden until this check succeeds.':
    '我们无法加载你的孩子信息。在检查成功之前，计划操作会保持隐藏。',
  'No active child profile': '没有启用中的孩子资料',
  'Add or reactivate a child profile before you create or import an HSC plan.':
    '创建或导入 HSC 计划之前，请先添加或重新启用一个孩子资料。',
  'Loading HSC plans…': '正在加载 HSC 计划…',
  'We could not load the family HSC plans.': '我们无法加载家庭的 HSC 计划。',
  'No HSC plans yet': '还没有 HSC 计划',
  'Create a plan above when your child is ready to track Year 12 assessments.':
    '孩子准备好记录 12 年级考核时，就在上方创建一个计划。',
  'Finish saving your calculator': '完成保存你的计算结果',
  'Confirm what should enter the family plan': '确认哪些内容要放进家庭计划',
  'Nothing is saved until you choose the child and course, then confirm a real name and date for every assessment.':
    '在你选择孩子和课程，并为每次考核确认真实的名称和日期之前，不会保存任何内容。',
  'We could not check this save link.': '我们无法检查这个保存链接。',
  'Checking the one-use save link…': '正在检查一次性保存链接…',
  'Year 12 child': '12 年级的孩子',
  'School year': '学年',
  'Course name used by the school': '学校使用的课程名称',
  '{achieved}/{maximum} · weight {weight}%': '{achieved}/{maximum} · 权重 {weight}%',
  'Assessment name': '考核名称',
  'Assessment date': '考核日期',
  'We could not import this calculation.': '我们无法导入这次计算结果。',
  'Confirm and save to this child': '确认并保存到这个孩子名下',
  'Start another plan': '再建一个计划',
  'Create plan': '创建计划',
  'We could not create that plan.': '我们无法创建这个计划。',
  '{year} HSC plan': '{year} HSC 计划',
  'Plan active': '计划已启用',
  'Setup in progress': '设置中',
  "Delete {name}'s {year} plan": '删除 {name} 的 {year} 计划',
  'Delete plan': '删除计划',
  "This removes every subject and assessment in {name}'s {year} plan. It cannot be undone.":
    '这会删除 {name} 的 {year} 计划中的所有科目和考核，且无法撤销。',
  'Activation needs at least two subjects and one confirmed future assessment.':
    '启用计划至少需要两个科目和一次已确认的未来考核。',
  '{count} unit': '{count} 个单元',
  '{count} units': '{count} 个单元',
  '{units} · {weight}% completed weight': '{units} · 已完成权重 {weight}%',
  'Delete {name}': '删除 {name}',
  'Delete subject': '删除科目',
  'This removes {name} and its 1 saved assessment. It cannot be undone.':
    '这会删除 {name} 及其 1 次已保存的考核，且无法撤销。',
  'This removes {name} and its {count} saved assessments. It cannot be undone.':
    '这会删除 {name} 及其 {count} 次已保存的考核，且无法撤销。',
  'Running result on completed work only—not a predicted HSC mark.':
    '累计成绩仅基于已完成的作业——不是预测的 HSC 分数。',
  '{date} · weight {weight}% · {result}': '{date} · 权重 {weight}% · {result}',
  'result not entered': '尚未录入成绩',
  'This removes {name} and any mark saved against it. It cannot be undone.':
    '这会删除 {name} 及其已保存的分数，且无法撤销。',
  'Keep it': '保留',
  'We could not delete that.': '我们无法删除它。',
  'Add a subject': '添加科目',
  'School course name': '学校课程名称',
  'Add subject': '添加科目',
  'The HSC course list is not ready, so no course can be chosen yet.':
    'HSC 课程列表还没准备好，所以暂时无法选择课程。',
  'We could not add that subject.': '我们无法添加这个科目。',
  'Add an assessment': '添加考核',
  'Task name': '任务名称',
  'Weight %': '权重 %',
  'Mark achieved': '得分',
  'Maximum mark': '满分',
  'We could not add that assessment.': '我们无法添加这次考核。',
  'Save assessment': '保存考核',
  'Could not load this information': '无法加载这些信息',
  'Choose a course': '请选择课程',
  'Enter the course name used by the school': '请输入学校使用的课程名称',
  'Enter the task name': '请输入任务名称',
  'Enter the weight': '请输入权重',
  'Completed tasks need both marks': '已完成的任务需要同时填写得分和满分',
  'Name this assessment': '请为这次考核命名',
  'Set up your family before saving a pathway': '请先设置家庭，再保存学习路径',
  'Set up my family': '设置我的家庭',
  'Checking pathway planning availability…': '正在检查学习路径规划是否可用…',
  'Not open yet': '尚未开放',
  'My Study Plan is currently unavailable': '“我的学习计划”目前不可用',
  'Your family data has not been changed. Please use the public school and course information while the private planner is closed.':
    '你的家庭数据没有被改动。私人规划工具关闭期间，请使用公开的学校和课程信息。',
  'View public school information': '查看公开的学校信息',
  'Start with possible university courses, then check which senior subjects need attention. This plan does not predict an ATAR or guarantee admission.':
    '先从可能的大学课程开始，再查看哪些高中科目需要关注。这份计划不会预测 ATAR，也不保证被录取。',
  'We could not load the private family plan. No planning data was changed.':
    '我们无法加载这份家庭私人计划。没有任何规划数据被改动。',
  'Add an active child profile before creating a plan.':
    '创建计划之前，请先添加一个启用中的孩子资料。',
  'Loading saved plans…': '正在加载已保存的计划…',
  'No saved plan yet': '还没有已保存的计划',
  'Create a draft above. You can save it before every choice is final.':
    '先在上方创建一份草稿。不必等所有选择都定下来就可以保存。',
  'Start a private plan': '开始一份私人计划',
  'Current year': '当前年级',
  'University entry year': '大学入学年份',
  'Senior curriculum': '高中课程体系',
  Undecided: '未定',
  'IB Diploma': 'IB 文凭课程',
  Other: '其他',
  Destination: '目的地',
  Australia: '澳大利亚',
  Overseas: '海外',
  'Australia and overseas': '澳大利亚和海外',
  'Creating…': '正在创建…',
  'Create draft plan': '创建草稿计划',
  'We could not create the plan.': '我们无法创建这份计划。',
  'Active plan': '启用中的计划',
  'Draft plan': '草稿计划',
  'School year not set': '未设置年级',
  UNDECIDED: '未定',
  OTHER: '其他',
  'IB DP': 'IB DP',
  undecided: '未定',
  australia: '澳大利亚',
  overseas: '海外',
  both: '澳大利亚和海外',
  'Possible university courses': '可能的大学课程',
  'No course saved yet. Start with a possible destination, then work backwards.':
    '还没有保存课程。先从一个可能的目标出发，再倒推需要做什么。',
  'Remove {name}': '移除 {name}',
  'Source changed. Review this course again.': '来源已变更，请重新查看这门课程。',
  'Published university facts are unavailable. Existing choices remain unchanged.':
    '已发布的大学信息暂不可用，已有的选择保持不变。',
  'No reviewed university prerequisite dataset is published.':
    '目前没有发布经过核对的大学先修要求数据。',
  'Add a reviewed course': '添加一门已核对的课程',
  'The plan changed or could not be saved. Refresh and try again.':
    '计划已发生变化或无法保存。请刷新后重试。',
  'Revision {number}': '版本 {number}',
  'Activate plan': '启用计划',
  'Delete this private plan permanently?': '要永久删除这份私人计划吗？',
  'Course choices, results, questions, decisions, actions and reviews are removed immediately. Only a non-content audit receipt remains.':
    '课程选择、成绩、问题、决定、行动和复查记录会立即删除，只保留一条不含内容的审计回执。',
  'Yes, delete permanently': '是，永久删除',
  'Keep plan': '保留计划',
  'The plan was not deleted. Refresh and try again.': '计划没有被删除。请刷新后重试。',
  'Raw VCE study score': 'VCE 原始学习分数',
  'School grade': '学校成绩',
  'IB predicted grade': 'IB 预测成绩',
  'IB final grade': 'IB 最终成绩',
  'Other result': '其他成绩',
  'Optional results': '可选成绩',
  'Family-entered and private. Raw VCE study scores are never shown as scaled scores or used to predict an ATAR. Adviser sharing is off by default and must be enabled per result.':
    '由家庭录入，仅限私人查看。VCE 原始学习分数不会被显示为换算后的分数，也不会用来预测 ATAR。默认不与顾问分享，需要逐项成绩单独开启。',
  'Saved subject': '已保存的科目',
  'saved subject': '已保存的科目',
  'Shared with invited adviser': '已分享给受邀顾问',
  'Private to family': '仅家庭可见',
  'Make private': '设为私密',
  'Share with adviser': '分享给顾问',
  'Remove result for {name}': '移除 {name} 的成绩',
  'Choose subject': '选择科目',
  'Result type': '成绩类型',
  'Add and share result': '添加并分享成绩',
  'Add private result': '添加私密成绩',
  'Share this result only with an actively invited adviser who has the results scope':
    '仅与已受邀、且有查看成绩权限的顾问分享这项成绩',
  'The result could not be saved. Refresh and try again.': '无法保存这项成绩。请刷新后重试。',
  'Plan summary': '计划概要',
  'Course shortlist': '候选课程',
  'Subject plan': '科目计划',
  'Only results explicitly shared by parent': '仅限家长明确分享的成绩',
  'Questions and consultation notes': '问题和咨询记录',
  'Adviser suggestions': '顾问建议',
  'Invited planning reviews': '受邀的规划审阅',
  'A class or school relationship never grants access. Invite one verified teacher or adviser to this child and plan, choose the exact scope, and set an expiry.':
    '班级或学校关系不会自动授予访问权限。请邀请一位经过验证的老师或顾问查看这个孩子和这份计划，选择准确的范围，并设置有效期。',
  '{status} · expires {date}': '{status} · {date} 到期',
  active: '生效中',
  declined: '已拒绝',
  revoked: '已撤销',
  completed: '已完成',
  'Revoke access': '撤销访问权限',
  'plan summary': '计划概要',
  'course shortlist': '候选课程',
  'subject plan': '科目计划',
  'results shared by parent': '家长分享的成绩',
  'consultation notes': '咨询记录',
  suggestions: '建议',
  question: '问题',
  comment: '评论',
  suggestion: '建议',
  'Parent decision: {status}': '家长决定：{status}',
  'no decision': '未决定',
  accepted: '已接受',
  'to check': '待核实',
  'Verified adviser email': '已验证顾问的邮箱',
  'Expiry date': '到期日期',
  Purpose: '用途',
  'Information this adviser can access': '这位顾问可以查看的信息',
  'Send invitation': '发送邀请',
  'Invitation details could not be saved. No access change was assumed.':
    '无法保存邀请信息。没有假定任何访问权限已变更。',
  'Requirement check': '要求核对',
  'This checks saved subject identifiers. A name-only match still needs confirmation, and a future score is never treated as achieved.':
    '这里核对的是已保存科目的标识。仅名称相同仍需要确认，未来的成绩也不会被当作已经达到。',
  'Source changed': '来源已变更',
  'Covered for planning': '规划上已满足',
  'Check equivalence': '需核对是否等同',
  Unknown: '未知',
  'all of': '全部满足',
  'one of': '满足其一',
  'Senior subjects': '高中科目',
  'These are family choices, not confirmed school offerings.':
    '这些是家庭的选择，不代表学校一定开设。',
  'Subject name': '科目名称',
  'e.g. Mathematical Methods': '例如 Mathematical Methods',
  'Enter a subject name': '请输入科目名称',
  'Year or level': '年级或级别',
  Optional: '可选',
  'Planning state': '规划状态',
  Proposed: '拟选',
  Current: '当前',
  Alternative: '备选',
  'The subject list could not be saved. Refresh and try again.': '无法保存科目列表。请刷新后重试。',
  'Questions to check': '待核实的问题',
  'Add a private question': '添加一个私人问题',
  'Add question': '添加问题',
  'Decision log': '决定记录',
  'Keep the reason behind a family choice with the plan revision it used.':
    '把家庭做出某个选择的原因，连同当时的计划版本一起记录下来。',
  '{type} · {date} · revision {number}': '{type} · {date} · 版本 {number}',
  'subject choice': '科目选择',
  curriculum: '课程体系',
  destination: '目的地',
  other: '其他',
  'Decision type': '决定类型',
  Curriculum: '课程体系',
  'Subject choice': '科目选择',
  'What did the family decide?': '家庭做了什么决定？',
  'Decision date': '决定日期',
  'Record decision': '记录决定',
  'Next actions': '下一步行动',
  parent: '家长',
  child: '孩子',
  family: '家庭',
  'due {date}': '{date} 到期',
  Owner: '负责人',
  Parent: '家长',
  'Due date': '截止日期',
  'Add action': '添加行动',
  'Record a review': '记录一次复查',
  'A review records this plan revision. It does not change public facts.':
    '复查会记录当前的计划版本，不会改变公开信息。',
  'Next review date': '下次复查日期',
  'Private note': '私人备注',
  'Save review': '保存复查',
  '1 review recorded': '已记录 1 次复查',
  '{count} reviews recorded': '已记录 {count} 次复查',
  'Private planning details could not be saved. Refresh and try again.':
    '无法保存私人规划信息。请刷新后重试。',
  Next: '下一步',
  Later: '之后',
  'Earlier foundation': '早期基础',
  'Continue this interest': '延续这个兴趣',
  'Grow this capability': '提升这项能力',
  'Build a bigger system': '搭建更大的系统',
  'Transfer the method': '迁移所学方法',
  'Dismiss course details': '关闭课程详情',
  'Close course details': '关闭课程详情',
  'What your child will make': '孩子将做出什么',
  'Your learning route': '你的学习路线',
  'See where this project can lead.': '看看这个项目可以通向哪里。',
  'Open previous project {title}': '打开上一个项目：{title}',
  'From · L{level}': '来自 · L{level}',
  'This project can be a clear entry point into the pathway.':
    '这个项目可以作为进入学习路径的清晰起点。',
  'Now · L{level}': '当前 · L{level}',
  'Open next project {title}': '打开下一个项目：{title}',
  'Next · L{level}': '下一步 · L{level}',
  'Capstone outcome': '收官成果',
  'Use the project evidence to decide the next personal goal.':
    '用项目成果作为依据，决定下一个个人目标。',
  'How this course practises all 8 capabilities': '这门课程如何练习全部 8 项能力',
  'Longer bars mean more lesson time, practice and visible project evidence.':
    '条越长，表示课时、练习和可见的项目成果越多。',
  '{score} out of 5': '5 分中的 {score} 分',
  'Your child {action}.': '孩子会{action}。',
  'Key practice': '重点练习',
  'Evidence your family may see': '你的家庭可能看到的成果',
  'A working project, feedback notes, an improved version and a final Challenge or creator pitch.':
    '一个能运行的作品、反馈记录、改进后的版本，以及最后的挑战赛作品或创作者讲解。',
  'Connected projects': '相关联的项目',
  'Pathway status:': '路径状态：',
  'Planned course. Course connections show practice and progression, not a prerequisite or guaranteed result.':
    '计划中的课程。课程之间的连接表示练习与进阶关系，不是先修要求，也不保证任何结果。',
  'Saved for {name} · remove': '已为 {name} 保存 · 移除',
  'Saved for your child · remove': '已为孩子保存 · 移除',
  'Save for {name}': '为 {name} 保存',
  'Save for your child': '为孩子保存',
  'Saving is unavailable': '暂时无法保存',
  'Choose a child to save': '请选择一个孩子再保存',
  'Plan full · remove one project first': '计划已满 · 请先移除一个项目',
  'This project could not be saved. Your existing plan has not changed.':
    '无法保存这个项目，你已有的计划没有变化。',
  'View full course': '查看完整课程',
  'Strongest focus: {focus}': '最主要的重点：{focus}',
  'Building the pathway map…': '正在生成学习路径图…',
  'The pathway map could not load.': '学习路径图无法加载。',
  'Your saved child data has not changed.': '你已保存的孩子数据没有变化。',
  'Back to Explore': '返回探索',
  'Airbotix Pathway Navigator': 'Airbotix 学习路径导航',
  'See how one project can grow into the next.': '看看一个项目如何成长为下一个项目。',
  'Explore all eight Levels, follow the curves between related courses and choose the future capabilities your family wants to develop.':
    '浏览全部八个等级，沿着相关课程之间的曲线走，并选择你的家庭想培养的未来能力。',
  'Complete pathway': '完整学习路径',
  '8 Levels · {count} courses': '8 个等级 · {count} 门课程',
  'Projection {version}': '版本 {version}',
  'Pathway controls': '学习路径设置',
  'Plan for': '规划对象',
  'Choose by age': '按年龄选择',
  'Build a capability': '培养一项能力',
  'Choose a capability goal': '选择一个能力目标',
  'All capabilities': '全部能力',
  'Focus Level {level}, {identity}': '聚焦第 {level} 级：{identity}',
  '{count}/4 projects saved for {name}': '已为 {name} 保存 {count}/4 个项目',
  'Mint rings show a good fit now': '薄荷绿圆环表示现在很合适',
  'Interactive course network': '可交互的课程网络',
  'Follow the curves. Find the next project.': '沿着曲线走，找到下一个项目。',
  'Move over any course to light up where it came from, where it can lead and the capabilities it practises.':
    '把鼠标移到任意课程上，就会点亮它的来源、可以通向的去处，以及它练习的能力。',
  'Connection legend': '连线图例',
  'Related course': '相关课程',
  'Selected route': '已选路线',
  'Capability practice': '能力练习',
  'Vertical axis ↓': '纵轴 ↓',
  'Level & age': '等级与年龄',
  'Follow growth from L1 to L8.': '跟随成长，从 L1 走到 L8。',
  'Future capability horizontal axis': '未来能力横轴',
  'Filter courses by {label}': '按“{label}”筛选课程',
  'Tracing {title}': '正在追踪 {title}',
  'Level {level} courses': '第 {level} 级课程',
  'Level {level} · {identity}': '第 {level} 级 · {identity}',
  'One project choice': '1 个项目可选',
  '{count} project choices': '{count} 个项目可选',
  'Open {title} course details': '打开 {title} 课程详情',
  'Course {number}': '课程 {number}',
  'How to read the map:': '如何阅读这张图：',
  'faint curves show a related next project. Bright curves show the route you selected. Connections show practice and progression—not a guaranteed result or prerequisite.':
    '淡色曲线表示相关的下一个项目，亮色曲线表示你选中的路线。连线表示练习与进阶关系，不保证任何结果，也不是先修要求。',
  'Showing the connected route for {title}.': '正在显示 {title} 的关联路线。',
};
